import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { usePredictionStore } from 'stores/predictionStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PredictionsTable from '../components/PredictionsTable';
import { TableRow, ColumnConfig, ExportFormat } from '../types';
import { routes } from 'routes/routes';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import { asPrice } from 'utils/money';
import { formatScore } from '../utils/formatScore';

const GroupedRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { predictionResult, isLoading, error } = usePredictionStore();

  const columns: ColumnConfig[] = [
    {
      key: LOAD_TABLE_FIELDS.internal_id, 
      header: "ID #1", 
      cell: { type: 'readonly' },
      width: 370,
      resizable: true
    },
    {
      key: LOAD_TABLE_FIELDS.version_id,
      header: "ID #2", 
      cell: { type: 'readonly' },
      width: 120
    },
    {
      key: LOAD_TABLE_FIELDS.title, 
      header: "Title", 
      cell: { type: 'readonly' },
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      resizable: true
    },
    {
      key: LOAD_TABLE_FIELDS.release_date, 
      header: "Release Date", 
      cell: { type: 'readonly' },
      width: 185
    },
    {
      key: LOAD_TABLE_FIELDS.box_office_multiple, 
      header: "Box Office Multiple", 
      cell: { type: 'readonly' },
      width: 100
    },
    {
      key: LOAD_TABLE_FIELDS.resulting_gross, 
      header: "Resulting Gross", 
      cell: { type: 'readonly' },
      width: 140,
      formatter: (value) => `$${asPrice(value)}`,
    },
    {
      key: LOAD_TABLE_FIELDS.score, 
      header: "Score", 
      cell: { type: 'readonly' },
      width: 100
    },
    // Static columns from grouped_scores
    {
      key: "budget_score",
      header: "Budget",
      cell: { type: 'readonly' },
      width: 145,
      formatter: formatScore,
    },
    {
      key: "month_score",
      header: "Month",
      cell: { type: 'readonly' },
      width: 100,
      formatter: formatScore,
    },
    {
      key: "mpaa_score",
      header: "MPAA",
      cell: { type: 'readonly' },
      width: 100,
      formatter: formatScore,
    },
    {
      key: "format_score",
      header: "Format",
      cell: { type: 'readonly' },
      width: 100,
      formatter: formatScore,
    },
    {
      key: "genre_cnsd_score",
      header: "Genre CNSD",
      cell: { type: 'readonly' },
      width: 135,
      formatter: formatScore,
    },
    {
      key: "topic_cnsd_score",
      header: "Topic CNSD",
      cell: { type: 'readonly' },
      width: 120,
      formatter: formatScore,
    },
    {
      key: "actors_cnsd_score",
      header: "Actor CNSD",
      cell: { type: 'readonly' },
      width: 120,
      formatter: formatScore,
    },
    {
      key: "directors_cnsd_score",
      header: "Director CNSD",
      cell: { type: 'readonly' },
      width: 140,
      formatter: formatScore,
    },
    {
      key: "producers_cnsd_score",
      header: "Producer CNSD",
      cell: { type: 'readonly' },
      width: 150,
      formatter: formatScore,
    },
    {
      key: "writers_cnsd_score",
      header: "Writer CNSD",
      cell: { type: 'readonly' },
      width: 130,
      formatter: formatScore,
    },
    {
      key: "stars_cnsd_score",
      header: "Star CNSD",
      cell: { type: 'readonly' },
      width: 130,
      formatter: formatScore,
    },
    {
      key: "cohesion_cnsd_score",
      header: "Cohesion CNSD",
      cell: { type: 'readonly' },
      width: 150,
      formatter: formatScore,
    },
    {
      key: "model_base_score",
      header: "Model Base",
      cell: { type: 'readonly' },
      width: 120,
      formatter: formatScore,
    }
  ];

  useEffect(() => {
    if (!predictionResult?.movies) return;

    try {
      const groupedData = predictionResult.movies.map((movie: any) => {
        const groupedScore = movie.grouped_scores || {};
        
        const rowData: Record<string, any> = {
          [LOAD_TABLE_FIELDS.internal_id]: movie.internal_id || '',
          [LOAD_TABLE_FIELDS.version_id]: movie.version_id || '',
          [LOAD_TABLE_FIELDS.title]: movie.title || '',
          [LOAD_TABLE_FIELDS.release_date]: movie.release_date || '',
          [LOAD_TABLE_FIELDS.pred_roi]: movie.pred_roi || '',
          [LOAD_TABLE_FIELDS.box_office_multiple]: movie.box_office_multiple || '',
          [LOAD_TABLE_FIELDS.resulting_gross]: movie.resulting_gross || '',
          [LOAD_TABLE_FIELDS.score]: movie.score || '',
          
          budget_score: groupedScore.budget_score || '',
          month_score: groupedScore.month_score || '',
          mpaa_score: groupedScore.mpaa_score || '',
          format_score: groupedScore.format_score || '',
          genre_cnsd_score: groupedScore.genre_cnsd_score || '',
          topic_cnsd_score: groupedScore.topic_cnsd_score || '',
          actors_cnsd_score: groupedScore.actors_cnsd_score || '',
          directors_cnsd_score: groupedScore.directors_cnsd_score || '',
          producers_cnsd_score: groupedScore.producers_cnsd_score || '',
          writers_cnsd_score: groupedScore.writers_cnsd_score || '',
          stars_cnsd_score: groupedScore.stars_cnsd_score || '',
          cohesion_cnsd_score: groupedScore.cohesion_cnsd_score || '',
          model_base_score: groupedScore.model_base_score || ''
        };

        return rowData;
      });
      
      setTableData(groupedData);
    } catch (parseError) {
      console.error('Error processing prediction data:', parseError);
    }
  }, [predictionResult]);

  const selectedRow = (location.state as any)?.selectedRow;
  const [tableData, setTableData] = useState<TableRow[]>([
    selectedRow || {
      [LOAD_TABLE_FIELDS.internal_id]: "",
      [LOAD_TABLE_FIELDS.version_id]: "",
      [LOAD_TABLE_FIELDS.title]: "",
      [LOAD_TABLE_FIELDS.release_date]: "",
      [LOAD_TABLE_FIELDS.pred_roi]: "",
      [LOAD_TABLE_FIELDS.box_office_multiple]: "",
      [LOAD_TABLE_FIELDS.resulting_gross]: "",
      [LOAD_TABLE_FIELDS.score]: ""
    }
  ]);

  const handleCellChange = (rowIndex: number, column: string, value: string | any[]): void => {};

  const addNewRow = (): void => {};

  const handleExport = (format: ExportFormat): void => {};

  const handleBackToPredict = (): void => {
    history.push(`${routes.PREDICTIONS_ENGINE}/predict`);
  };

  const isExportAllowed = predictionResult?.movies?.some(movie => movie.is_export_allowed === true) || false;

  const actionButtons = [
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={!isExportAllowed}>
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleExport('CSV')}>
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,

    <Button variant="default" onClick={handleBackToPredict}>
      Back to Predict
    </Button>
  ];

  if (!predictionResult?.movies || predictionResult.movies.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h4>No Grouped Data Available</h4>
        <p>No prediction results found. Please run a prediction first.</p>
        <Button variant="default" onClick={handleBackToPredict}>
          Back to Predict
        </Button>
      </div>
    );
  }

  return (
    <PredictionsTable
      actionButtons={actionButtons}
      columns={columns}
      tableData={tableData}
      onCellChange={handleCellChange}
      onAddRow={addNewRow}
      disableAddButton={true}
      disableModelSelection={true}
    />
  );
};

export default GroupedRoute;